import { LayerProps } from 'shared/map-exports';
import { useMemo } from 'react';
import {
  ExternalFeatureLayerProps,
  ExternalLayerStylesInput,
} from './ExternalFeatureLayer.types';
import { EmbeddedStylePolygonLayer } from './EmbeddedStylePolygonLayer';

type PolygonLayersProps = ExternalFeatureLayerProps;

const getPolygonFillLayerStyles = (
  input: ExternalLayerStylesInput,
): LayerProps => {
  if (input.displayStyleType !== 'simple_single_color') {
    throw new Error(
      'Polygon layer styles require simple_single_color display style',
    );
  }
  const { sourceLayer, color } = input;
  return {
    type: 'fill',
    'source-layer': sourceLayer,
    paint: {
      'fill-color': color,
      'fill-opacity': 0.25,
    },
  };
};

const getPolygonStrokeLayerStyles = (
  input: ExternalLayerStylesInput,
): LayerProps => {
  if (input.displayStyleType !== 'simple_single_color') {
    throw new Error(
      'Polygon stroke layer styles require simple_single_color display style',
    );
  }
  const { sourceLayer, color } = input;
  return {
    type: 'line',
    'source-layer': sourceLayer,
    paint: {
      'line-color': color,
      'line-opacity': 0.75,
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 18, 6],
    },
  };
};

export const SimlePolygonLayer = (props: PolygonLayersProps): JSX.Element => {
  const { sourceId, featureLayer, visible } = props;
  const { shortName, displayStyleData } = featureLayer;

  const updatedFeatureLayer = useMemo(() => {
    const fillStyle = getPolygonFillLayerStyles({
      sourceLayer: shortName,
      ...displayStyleData,
    });
    const strokeStyle = getPolygonStrokeLayerStyles({
      sourceLayer: shortName,
      ...displayStyleData,
    });
    const featureLayerCopy = { ...featureLayer };
    featureLayerCopy.displayStyleData = {
      layers: [
        { ...fillStyle, id: `${sourceId}_fill` },
        { ...strokeStyle, id: `${sourceId}_stroke` },
      ],
      clickListenerLayerId: `${sourceId}_fill`,
      displayStyleType: 'embedded',
    };
    return featureLayerCopy;
  }, [sourceId, shortName, displayStyleData, featureLayer]);

  return (
    <EmbeddedStylePolygonLayer
      visible={visible}
      sourceId={sourceId}
      featureLayer={updatedFeatureLayer}
    />
  );
};
